import { useState, useCallback, useRef } from 'react';

import { useCanvasImages } from '@/hooks/useCanvasImages';
import { getContext } from '@/miniappContext';
import { isDriveChooserAvailable, openDriveChooser } from '@/services/driveChooserService';
import { convertHeicToPng, isHeicFile } from '@/utils/convertHeic';
import { isImageFile } from '@/utils/fileTypes';
import { getSingleLayerImage, getCanvasImages, type CanvasImage } from '@/utils/replayUtils';
import showNotification, { NotificationType } from '@/utils/showNotification';

export interface UseImageSourcePickerOptions {
  /** Callback when an image is selected (URL-based from Drive or Canvas) */
  onImageUrl: (url: string) => void;
  /** Callback when a file is selected (from device) */
  onFile: (file: File) => void;
  /** Auto-import single image from canvas without showing picker */
  autoImportSingle?: boolean;
}

export interface UseImageSourcePickerResult {
  // Canvas picker state
  isCanvasPickerOpen: boolean;
  canvasImages: CanvasImage[];
  closeCanvasPicker: () => void;
  handleCanvasPickerSelect: (url: string) => void;

  // Source availability
  isDriveAvailable: boolean;
  canvasImageCount: number;

  // Action handlers
  handleSelectFromDevice: () => void;
  handleSelectFromDrive: () => Promise<void>;
  handleSelectFromCanvas: () => Promise<void>;

  // File input ref and handler
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * Reusable hook for image source selection (Device, Drive, Canvas).
 * Works together with the ImageSourceMenu DS Select component.
 */
export function useImageSourcePicker({ onImageUrl, onFile, autoImportSingle = true }: UseImageSourcePickerOptions): UseImageSourcePickerResult {
  // Canvas picker state
  const [isCanvasPickerOpen, setIsCanvasPickerOpen] = useState(false);
  const [canvasImages, setCanvasImages] = useState<CanvasImage[]>([]);

  // File input ref
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Check service availability
  const isDriveAvailable = isDriveChooserAvailable();
  const { imageCount: canvasImageCount } = useCanvasImages();

  // Canvas picker handlers
  const closeCanvasPicker = useCallback(() => {
    setIsCanvasPickerOpen(false);
  }, []);

  const handleCanvasPickerSelect = useCallback(
    (url: string) => {
      onImageUrl(url);
      setIsCanvasPickerOpen(false);
    },
    [onImageUrl],
  );

  // File input handler
  const handleFileInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.files?.[0];
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (!raw) return;
      if (!isImageFile(raw)) {
        showNotification(NotificationType.uploadError, 'Only image files are supported.');
        return;
      }
      const file = isHeicFile(raw) ? await convertHeicToPng(raw) : raw;
      onFile(file);
    },
    [onFile],
  );

  // Device selection handler — triggers hidden file input
  const handleSelectFromDevice = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Drive selection handler
  const handleSelectFromDrive = useCallback(async () => {
    try {
      const result = await openDriveChooser({
        multiselect: false,
        filters: ['PHOTO'],
      });

      if (result.success && result.files.length > 0) {
        onImageUrl(result.files[0].sourceUrl);
      } else if (result.error) {
        const context = getContext();
        context.layout.notification.show({
          title: 'Error',
          description: result.error,
          type: 'error',
        });
      }
    } catch (error) {
      console.error('[useImageSourcePicker] Drive selection error:', error);
    }
  }, [onImageUrl]);

  // Canvas selection handler
  const handleSelectFromCanvas = useCallback(async () => {
    try {
      if (autoImportSingle) {
        const singleImage = await getSingleLayerImage();
        if (singleImage) {
          onImageUrl(singleImage.url);
          return;
        }
      }

      const images = await getCanvasImages();
      if (images.length === 0) return;

      if (images.length === 1 && autoImportSingle) {
        onImageUrl(images[0].url);
        return;
      }

      setCanvasImages(images);
      setIsCanvasPickerOpen(true);
    } catch (error) {
      console.error('[useImageSourcePicker] Canvas import error:', error);
    }
  }, [onImageUrl, autoImportSingle]);

  return {
    isCanvasPickerOpen,
    canvasImages,
    closeCanvasPicker,
    handleCanvasPickerSelect,
    isDriveAvailable,
    canvasImageCount,
    handleSelectFromDevice,
    handleSelectFromDrive,
    handleSelectFromCanvas,
    fileInputRef,
    handleFileInputChange,
  };
}
